/* eslint-disable @nx/enforce-module-boundaries -- ts-rs binding types, erased at compile
   time; nx files the binding crates under the root project and reads a cycle. */
import type { UserRecordingRequest } from 'tauri-plugin-rpa-recorder/bindings/UserRecordingRequest';
/* eslint-enable @nx/enforce-module-boundaries */
import type { AgentModeMetadata, Capability, SessionId, SessionSummary } from './types';

export const RPA_RECORDER_PROTOCOL = {
  commands: {
    start: 'recording_start',
    restart: 'recording_restart',
    stop: 'recording_stop',
    cancel: 'recording_cancel',
    arm: 'recording_arm',
    disarm: 'recording_disarm',
    armState: 'recording_arm_state',
    toolbarSetMode: 'recording_toolbar_set_mode',
    status: 'recording_status',
    list: 'recording_list',
    discard: 'recording_discard',
    nameSubmit: 'recording_name_submit',
    agentPermissionAck: 'recording_agent_permission_ack',
    prepareUpload: 'recording_prepare_upload',
    retryUpload: 'recording_retry_upload',
    performUpload: 'recording_perform_upload',
    getWorkflow: 'recording_get_workflow',
    getUploadState: 'recording_get_upload_state',
    markAnalysisReady: 'recording_mark_analysis_ready',
    markAnalysisFailed: 'recording_mark_analysis_failed',
  },
  events: {
    sessionStateChanged: 'recording:session:state_changed',
    armStateChanged: 'recording:session:arm_state_changed',
    platformHealthChanged: 'recording:platform:health_changed',
    frameCaptureMissed: 'recording:session:frame_capture_missed',
    toolbarHoverChanged: 'recording:toolbar:hover_changed',
    uploadStateChanged: 'recording:upload:state_changed',
    uploadProgress: 'recording:upload:progress',
    analysisStatusChanged: 'recording:analysis:status_changed',
    nativeStopRequested: 'recording:session:native_stop_requested',
    narrationMicStateChanged: 'recording:session:narration_mic_state_changed',
    narrationAudioLevels: 'recording:session:narration_audio_levels',
    agentPermissionRequested: 'recording:agent:permission_requested',
  },
  engineErrors: {
    permissionDenied: 'RECORDING_PERMISSION_DENIED',
    alreadyActive: 'RECORDING_ALREADY_ACTIVE',
    notActive: 'RECORDING_NOT_ACTIVE',
    sessionInFlight: 'RECORDING_SESSION_IN_FLIGHT',
    failedSessionPendingDiscard: 'RECORDING_FAILED_SESSION_PENDING_DISCARD',
    stopTimedOut: 'RECORDING_STOP_TIMED_OUT',
    platformTapDisabled: 'RECORDING_PLATFORM_TAP_DISABLED',
    nameInvalid: 'RECORDING_NAME_INVALID',
    nameTooLong: 'RECORDING_NAME_TOO_LONG',
    notFound: 'RECORDING_NOT_FOUND',
    windowFailed: 'RECORDING_WINDOW_FAILED',
    agentPermissionUnknownRequest: 'RECORDING_AGENT_PERMISSION_UNKNOWN_REQUEST',
    agentPermissionMismatch: 'RECORDING_AGENT_PERMISSION_MISMATCH',
    agentPermissionWrongWebview: 'RECORDING_AGENT_PERMISSION_WRONG_WEBVIEW',
  },
} as const;

export type RpaRecorderCommandName =
  (typeof RPA_RECORDER_PROTOCOL.commands)[keyof typeof RPA_RECORDER_PROTOCOL.commands];

export type EngineSessionState = 'started' | 'stopping' | 'stopped' | 'failed';

export interface SessionStateChangedPayload {
  readonly sessionId: SessionId;
  readonly state: EngineSessionState;
  readonly eventCount?: number;
  readonly errorCode?: string;
}

export interface NarrationMicStateChangedPayload {
  readonly sessionId: SessionId;
  readonly micLive: boolean;
}

/**
 * Five frequency bands, each on a 0–50 scale. The arity mirrors the native
 * `AudioLevelsEventPayload { levels: [f32; 5] }` and is fixed by that type —
 * consumers index specific bands, so the tuple keeps that contract checkable.
 */
export type NarrationLevelBands = readonly [number, number, number, number, number];

/**
 * Per-frame narration mic meter (~30 fps), emitted by the native capture
 * visualizer only to the recording-toolbar window — the same shape as the
 * app-wide `AUDIO_LEVELS` stream, kept off it so the two meters cannot
 * overwrite each other.
 */
export interface NarrationAudioLevelsPayload {
  readonly source: string;
  readonly levels: NarrationLevelBands;
  readonly ts: number;
}

/**
 * Emitted by `recording_arm` / `recording_disarm` (which only toggle the
 * toolbar/overlay window visibility — the capture engine stays idle). Both
 * window webviews own an independent bridge instance, so this event is how the
 * toolbar webview learns it has been armed without going through the engine
 * session lifecycle.
 *
 * Agent-mode arms carry the read-only correlation metadata;
 * `agent` is `null` for manual arms/disarms and absent on older backends.
 */
export interface ArmStateChangedPayload {
  readonly armed: boolean;
  readonly agent?: AgentModeMetadata | null;
}

/**
 * Returned by the `recording_arm_state` command. The bridge seeds `armed` from
 * this on init so the toolbar webview — whose JS may not run until the panel is
 * shown, after `arm_state_changed` has already fired — can recover the armed
 * state it missed, mirroring how `recording` is recovered from the engine
 * status seed. Agent mode survives toolbar webview remounts the same way:
 * the correlation metadata lives in Rust and is re-seeded on every init.
 */
export interface ArmStatePayload {
  readonly armed: boolean;
  readonly agent?: AgentModeMetadata | null;
}

/**
 * Correlated agent permission request emitted by the Rust agent
 * recording service to the main-window permission host.
 */
export interface AgentPermissionRequestedPayload {
  readonly sessionId: string;
  readonly toolCallId: string;
  readonly requestId: string;
}

/**
 * The acknowledgement the permission host sends back. `granted: false` covers
 * both an explicit denial and a preflight that found permissions missing (the
 * gate modal stays open so the user can grant and the agent can retry).
 */
export interface AgentPermissionAckRequest extends AgentPermissionRequestedPayload {
  readonly granted: boolean;
}

export type PlatformHealthPayload =
  | { readonly type: 'tap_disabled'; readonly reason: 'timed_out' | 'user_input' }
  | { readonly type: 'screen_capture_permission_revoked' };

export interface PlatformHealthChangedPayload {
  readonly sessionId: SessionId;
  readonly health: PlatformHealthPayload;
}

export type FrameCaptureMissKind = 'not_ready' | 'failed' | 'cancelled';

export interface FrameCaptureMissedPayload {
  readonly sessionId: SessionId;
  readonly kind: FrameCaptureMissKind;
  readonly message: string;
}

/**
 * Emitted by the recording-toolbar native panel when the cursor enters or leaves
 * the panel. This is the authoritative hover signal: native tracking fires
 * reliably across programmatic panel resizes, whereas the webview's DOM
 * `mouseenter` / `mouseleave` can desync once the panel is resized out from
 * under the cursor.
 */
export interface ToolbarHoverChangedPayload {
  readonly hovering: boolean;
}

/** Native capture-engine status. Toolbar arm state is carried separately by `ArmStatePayload`. */
export type EngineSessionStatus =
  | { type: 'idle' }
  | {
      type: 'recording';
      sessionId: SessionId;
      captureNarration: boolean;
      narrationMicLive: boolean;
    }
  | { type: 'failed'; sessionId: SessionId; errorCode: string };

export interface EngineCommandErrorPayload {
  readonly code: string;
  readonly message?: string;
  readonly missing?: readonly Capability[];
}

export interface RpaRecorderProtocolContract {
  commands: {
    start: {
      name: typeof RPA_RECORDER_PROTOCOL.commands.start;
      request: { request: UserRecordingRequest };
      response: SessionId;
    };
    stop: {
      name: typeof RPA_RECORDER_PROTOCOL.commands.stop;
      request: undefined;
      response: SessionSummary;
    };
    status: {
      name: typeof RPA_RECORDER_PROTOCOL.commands.status;
      request: undefined;
      response: EngineSessionStatus;
    };
  };
  events: {
    sessionStateChanged: {
      name: typeof RPA_RECORDER_PROTOCOL.events.sessionStateChanged;
      payload: SessionStateChangedPayload;
    };
    narrationMicStateChanged: {
      name: typeof RPA_RECORDER_PROTOCOL.events.narrationMicStateChanged;
      payload: NarrationMicStateChangedPayload;
    };
    narrationAudioLevels: {
      name: typeof RPA_RECORDER_PROTOCOL.events.narrationAudioLevels;
      payload: NarrationAudioLevelsPayload;
    };
    platformHealthChanged: {
      name: typeof RPA_RECORDER_PROTOCOL.events.platformHealthChanged;
      payload: PlatformHealthChangedPayload;
    };
    frameCaptureMissed: {
      name: typeof RPA_RECORDER_PROTOCOL.events.frameCaptureMissed;
      payload: FrameCaptureMissedPayload;
    };
  };
}

/** Compile-time guard: keep TS contract aligned with generated bindings. */
type _StartRequestMatchesBinding =
  RpaRecorderProtocolContract['commands']['start']['request']['request'] extends UserRecordingRequest
    ? UserRecordingRequest extends RpaRecorderProtocolContract['commands']['start']['request']['request']
      ? true
      : never
    : never;
const _startRequestMatchesBinding: _StartRequestMatchesBinding = true;
void _startRequestMatchesBinding;
